import Button from "@mui/joy/Button";
import React, { useEffect, useRef, useState } from "react";
import Confetti from "react-confetti";
import Polaroid from "../polaroid/Polaroid";
import Stamp, { StampOption } from "./Stamp";
import TriviaOptions from "./TriviaOptions";
import { TriviaQuestion } from "./TriviaQuestion";

interface TriviaRoundProps {
  question: TriviaQuestion;
  numberOfQuestions: number;
  score: number;
  makeGuessWithQuestion: (guess: string, question: TriviaQuestion) => void;
  goToNextQuestion: () => void;
  finishGame: () => void;
  guess: string | undefined;
  isSaving: boolean;
  roundNumber: number;
}

export default function TriviaRound({
  question,
  numberOfQuestions,
  score,
  makeGuessWithQuestion,
  goToNextQuestion,
  finishGame,
  guess,
  isSaving,
  roundNumber,
}: TriviaRoundProps) {
  const [answerPhotoVisible, setAnswerPhotoVisible] = useState(false);
  const [isCorrectGuess, setIsCorrectGuess] = useState(false);
  const confettiRef = useRef(null);

  const isLastQuestion = roundNumber === numberOfQuestions - 1;

  // Reset whether the guess was correct when a new round begins.
  useEffect(() => {
    setIsCorrectGuess(false);
  }, [roundNumber]);

  function makeGuess(newGuess: string) {
    makeGuessWithQuestion(newGuess, question);
    setIsCorrectGuess(question.isCorrect(newGuess));
  }

  function showPhoto() {
    setAnswerPhotoVisible(true);
  }

  return (
    <div className="h-full flex flex-col items-center justify-between py-4 text-blue-600 gap-4">
      <div className="flex flex-row w-full px-4 items-center justify-between">
        <div className="text-2xl font-bold mb-4 text-center">
          Question {roundNumber + 1} / {numberOfQuestions}
        </div>
        <div className="text-2xl font-bold mb-4 text-center">
          Score: {score}
        </div>
      </div>

      <div className="text-4xl font-bold mb-4 text-center p-4">
        {question.questionText}
      </div>

      {answerPhotoVisible ? (
        <Polaroid
          src={question.photo}
          caption={question.caption}
          background="bg-white"
          isClickable={false}
          size="large"
        />
      ) : (
        <div className="flex justify-center">
          {guess && <Stamp stampName={isCorrectGuess ? StampOption.CORRECT : StampOption.INCORRECT} />}
          <TriviaOptions
            makeGuess={makeGuess}
            question={question}
            guess={guess}
          />
        </div>
      )}

      <div className="flex flex-row-reverse gap-4">
        {!answerPhotoVisible && (
          <Button
            disabled={guess === undefined || isSaving}
            onClick={() => {
              showPhoto();
            }}
            size="lg"
            variant="solid"
          >
            Show Photo
          </Button>
        )}

        <Button
          disabled={guess === undefined || isSaving}
          onClick={() => {
            setAnswerPhotoVisible(false);
            if (isLastQuestion) {
              finishGame();
            } else {
              goToNextQuestion();
            }
          }}
          size="lg"
          variant={answerPhotoVisible ? "solid" : "soft"}
        >
          {isLastQuestion ? "Finish" : "Next Question"}
        </Button>
      </div>

      {isCorrectGuess && <Confetti ref={confettiRef} />}
    </div>
  );
}
